import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  cancelBrowserLogin,
  deleteServiceCredential,
  getServiceCredentials,
  reconnectServiceCredential,
  saveServiceApiKey,
  saveServiceCredential,
  sendServiceCredentialCode,
  startBrowserLogin,
} from '../services/api.js'

/** États pendant lesquels le worker travaille : on rafraîchit pour suivre la connexion. */
export const IN_PROGRESS_STATUSES = new Set([
  'connecting',
  'awaiting_code',
  'awaiting_approval',
  'browser_queued',
  'browser_ready',
])

const KEY = ['serviceCredentials']

/**
 * Services externes de Compte > Authentification et état de leur connexion.
 * Interroge l'API toutes les 3 s tant qu'une connexion est en cours.
 */
export function useServiceCredentials() {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: KEY,
    queryFn: getServiceCredentials,
    refetchInterval: (q) =>
      (q.state.data?.providers ?? []).some((p) => IN_PROGRESS_STATUSES.has(p.status)) ? 3000 : false,
  })

  // Chaque action renvoie l'état à jour du service : on le remplace directement dans la liste.
  const onSuccess = (updated) => {
    queryClient.setQueryData(KEY, (old) => ({
      providers: (old?.providers ?? []).map((p) => (p.provider === updated.provider ? updated : p)),
    }))
  }

  return {
    ...query,
    providers: query.data?.providers ?? [],
    save: useMutation({ mutationFn: saveServiceCredential, onSuccess }),
    saveApiKey: useMutation({ mutationFn: saveServiceApiKey, onSuccess }),
    sendCode: useMutation({ mutationFn: sendServiceCredentialCode, onSuccess }),
    reconnect: useMutation({ mutationFn: reconnectServiceCredential, onSuccess }),
    remove: useMutation({ mutationFn: deleteServiceCredential, onSuccess }),
    browserLogin: useMutation({ mutationFn: startBrowserLogin, onSuccess }),
    cancelBrowserLogin: useMutation({ mutationFn: cancelBrowserLogin, onSuccess }),
  }
}
